import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import HourlyHeatmap from '../components/analytics/HourlyHeatmap';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, ClipboardList, UserPlus, CalendarCheck, Activity, Loader2, TrendingUp,
} from 'lucide-react';

/** Rangos rápidos. El de 90 días es el que se abre por defecto. */
const QUICK_RANGES = [
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
  { label: '12 meses', days: 365 },
];

const GENDER_COLORS = { femenino: '#ec4899', masculino: '#2563eb', otro: '#a855f7' };
const FALLBACK_COLOR = '#94a3b8';

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days + 1);
  return date.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Panel del médico.
 *
 * Antes mostraba cuatro tarjetas, tres de ellas fijas en cero. Ahora responde
 * cuatro preguntas de gestión: cuánto atiendo, cuándo atiendo, a quién atiendo
 * y de qué. Todo sobre un rango de fechas que el médico elige.
 */
export default function DoctorDashboard() {
  const { user } = useAuth();
  const [range, setRange] = useState({ desde: isoDaysAgo(90), hasta: today() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: overview } = await api.get('/analytics/overview', { params: range });
      setData(overview);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cargar la analítica');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const applyQuickRange = (days) => setRange({ desde: isoDaysAgo(days), hasta: today() });

  /** Etiqueta del eje X según la granularidad que decidió el backend */
  const formatAxisDate = useMemo(() => {
    const granularity = data?.rango?.granularidad;
    return (value) => {
      const date = new Date(`${value}T12:00:00`);
      if (isNaN(date)) return value;
      if (granularity === 'month') {
        return date.toLocaleDateString('es-PE', { month: 'short', year: '2-digit' });
      }
      return date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
    };
  }, [data]);

  const kpis = data?.kpis;
  const cards = [
    { name: 'Consultas', value: kpis?.consultas ?? 0, icon: ClipboardList, color: 'bg-blue-600' },
    { name: 'Pacientes atendidos', value: kpis?.pacientes_atendidos ?? 0, icon: Users, color: 'bg-teal-600' },
    { name: 'Pacientes nuevos', value: kpis?.pacientes_nuevos ?? 0, icon: UserPlus, color: 'bg-violet-600' },
    {
      name: 'Promedio por día',
      value: kpis?.promedio_por_dia_activo ?? 0,
      icon: Activity,
      color: 'bg-amber-600',
      hint: `${kpis?.dias_activos ?? 0} días con atención`,
    },
    { name: 'Citas programadas', value: kpis?.citas_programadas ?? 0, icon: CalendarCheck, color: 'bg-slate-600' },
  ];

  const genderData = data?.demografia?.genero || [];
  const totalGender = genderData.reduce((sum, g) => sum + g.pacientes, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Hola, {user?.full_name?.split(' ')[0] || 'doctor'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Cómo va tu consulta en el periodo seleccionado</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {QUICK_RANGES.map(({ label, days }) => {
            const active = range.desde === isoDaysAgo(days) && range.hasta === today();
            return (
              <Button key={label} size="sm" variant={active ? 'default' : 'outline'}
                      onClick={() => applyQuickRange(days)}>
                {label}
              </Button>
            );
          })}
          <Input type="date" value={range.desde} max={range.hasta} className="w-auto"
                 aria-label="Desde"
                 onChange={(e) => setRange(r => ({ ...r, desde: e.target.value }))} />
          <span className="text-slate-400 text-sm">a</span>
          <Input type="date" value={range.hasta} min={range.desde} max={today()} className="w-auto"
                 aria-label="Hasta"
                 onChange={(e) => setRange(r => ({ ...r, hasta: e.target.value }))} />
        </div>
      </div>

      {loading && (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {cards.map(({ name, value, icon: Icon, color, hint }) => (
              <Card key={name}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500 truncate">{name}</p>
                      <p className="text-2xl font-semibold text-slate-800 mt-1">{value}</p>
                      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
                    </div>
                    <div className={`${color} p-2 rounded-lg shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-400" /> Consultas en el tiempo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.timeline} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fillConsultas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="fecha" tickFormatter={formatAxisDate} tick={{ fontSize: 11, fill: '#94a3b8' }}
                         tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }}
                         tickLine={false} axisLine={false} width={40} />
                  <Tooltip labelFormatter={formatAxisDate}
                           formatter={(value) => [value, 'Consultas']}
                           contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Area type="monotone" dataKey="consultas" stroke="#2563eb" strokeWidth={2}
                        fill="url(#fillConsultas)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Cuándo atiendes</CardTitle>
                <p className="text-xs text-slate-500">Día de la semana y hora. Sirve para ver dónde abrir o cerrar cupos.</p>
              </CardHeader>
              <CardContent><HourlyHeatmap data={data.heatmap} /></CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">A quién atiendes</CardTitle>
                <p className="text-xs text-slate-500">Pacientes distintos atendidos en el periodo.</p>
              </CardHeader>
              <CardContent>
                {totalGender === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">Sin pacientes en este rango</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <ResponsiveContainer width="100%" height={170}>
                      <PieChart>
                        <Pie data={genderData} dataKey="pacientes" nameKey="grupo"
                             innerRadius={38} outerRadius={68} paddingAngle={2}>
                          {genderData.map(entry => (
                            <Cell key={entry.grupo} fill={GENDER_COLORS[entry.grupo] || FALLBACK_COLOR} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} pacientes`, name]}
                                 contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                      </PieChart>
                    </ResponsiveContainer>

                    <div className="space-y-2">
                      {genderData.map(({ grupo, pacientes }) => (
                        <div key={grupo} className="flex items-center gap-2 text-sm">
                          <span className="w-3 h-3 rounded-sm shrink-0"
                                style={{ backgroundColor: GENDER_COLORS[grupo] || FALLBACK_COLOR }} />
                          <span className="capitalize text-slate-600 flex-1 truncate">{grupo}</span>
                          <span className="font-medium text-slate-800">{pacientes}</span>
                          <span className="text-slate-400 text-xs w-10 text-right">
                            {Math.round((pacientes / totalGender) * 100)}%
                          </span>
                        </div>
                      ))}
                      <div className="pt-2 mt-2 border-t text-sm flex justify-between">
                        <span className="text-slate-500">Nuevos / recurrentes</span>
                        <span className="font-medium text-slate-800">
                          {data.recurrencia.nuevos} / {data.recurrencia.recurrentes}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Grupo sanguíneo</CardTitle>
                <p className="text-xs text-slate-500">De los pacientes atendidos en el periodo.</p>
              </CardHeader>
              <CardContent>
                {!data.demografia.grupo_sanguineo?.length ? (
                  <p className="text-sm text-slate-500 py-8 text-center">Sin datos</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {data.demografia.grupo_sanguineo.map(({ grupo, pacientes }) => {
                      const missing = grupo === 'Sin registrar';
                      return (
                        <div key={grupo}
                             className={`px-3 py-2 rounded-lg border ${
                               missing ? 'border-dashed border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'
                             }`}>
                          <p className={`text-sm font-semibold ${missing ? 'text-slate-400' : 'text-red-700'}`}>
                            {grupo}
                          </p>
                          <p className="text-xs text-slate-500">
                            {pacientes} {pacientes === 1 ? 'paciente' : 'pacientes'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Grupos de edad</CardTitle></CardHeader>
              <CardContent>
                {data.demografia.edades.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">Sin datos de edad</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.demografia.edades} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <XAxis dataKey="grupo" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={40} />
                      <Tooltip formatter={(value) => [`${value} pacientes`, 'Pacientes']}
                               contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                      <Bar dataKey="pacientes" fill="#0d9488" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Diagnósticos más frecuentes</CardTitle></CardHeader>
              <CardContent>
                {data.top_diagnosticos.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">Sin diagnósticos registrados</p>
                ) : (
                  <div className="space-y-2">
                    {data.top_diagnosticos.map(({ diagnostico, consultas }) => {
                      const top = data.top_diagnosticos[0].consultas || 1;
                      return (
                        <div key={diagnostico} className="text-sm">
                          <div className="flex justify-between gap-2">
                            <span className="text-slate-700 truncate" title={diagnostico}>{diagnostico}</span>
                            <span className="text-slate-500 shrink-0">{consultas}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-1">
                            <div className="h-full bg-blue-500 rounded-full"
                                 style={{ width: `${(consultas / top) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
